import React, { useState, useEffect } from 'react';
import API from '../../api/API';
import './ARisultati.css';

const ARisultati = () => {
  const [edizioneSelezionata, setEdizioneSelezionata] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGironi, setExpandedGironi] = useState({});
  const [editingTurni, setEditingTurni] = useState({});
  const [deletingMatch, setDeletingMatch] = useState(null);

  useEffect(() => {
    const storedEdition = localStorage.getItem('selectedEdition');
    if (storedEdition) {
      const edizione = JSON.parse(storedEdition);
      setEdizioneSelezionata(edizione);
      loadPlanning(edizione.id);
    } else {
      setLoading(false);
      setError('Nessuna edizione selezionata');
    }
  }, []);

  const loadPlanning = async (idTorneo) => {
    try {
      setLoading(true);
      const response = await API.planning.create(idTorneo);

      if (response.isSuccess && response.data) {
        setMatches(response.data);
      } else {
        setError('Errore nel caricamento dei risultati');
      }
    } catch (err) {
      console.error('Error loading results:', err);
      setError('Errore durante il caricamento dei risultati');
    } finally {
      setLoading(false);
    }
  };

  const toggleGirone = (girone) => {
    setExpandedGironi(prev => ({
      ...prev,
      [girone]: !prev[girone]
    }));
  };

  const toggleEditTurno = (girone, turno) => {
    const key = `${girone}_${turno}`;
    setEditingTurni(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleDeleteMatch = async (match) => {
    if (!edizioneSelezionata) return;
    try {
      setDeletingMatch(match);
      const res = await API.partite.squadre(match.s1Id, match.s2Id, match.tipo);
      if (res.isSuccess && res.data) {
        const partite = Array.isArray(res.data) ? res.data : [res.data];
        const partita = partite.find(p => p.id > 0);
        if (partita) {
          await API.partite.delete(partita.id);
          await loadPlanning(edizioneSelezionata.id);
        }
      }
    } catch (err) {
      console.error('Error deleting match:', err);
    } finally {
      setDeletingMatch(null);
    }
  };

  const getGironeColor = (girone) => {
    const colors = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      'linear-gradient(135deg, #ffeaa7 0%, #fab1a0 100%)',
      'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
    ];

    const hash = girone.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const getWinner = (risultato) => {
    if (!risultato) return null;
    const { mete1, mete2 } = risultato;
    if (mete1 > mete2) return 1;
    if (mete2 > mete1) return 2;
    return null; // Pareggio
  };

  const formatRisultato1 = (risultato) => {
    if (!risultato) return '-';
    const { mete1, pieni1, vuoti1 } = risultato;
    return `${mete1 || 0}-${pieni1 || 0}-${vuoti1 || 0}`;
  };

  const formatRisultato2 = (risultato) => {
    if (!risultato) return '';
    const { mete2, pieni2, vuoti2 } = risultato;
    return `${mete2 || 0}-${pieni2 || 0}-${vuoti2 || 0}`;
  };

  // Funzione per determinare il tipo di girone e il suo valore di ordinamento
  const getGironeType = (gironeName) => {
    // Cerca numero nel nome (es. "1", "12")
    const numberMatch = gironeName.match(/^(\d+)$/);
    if (numberMatch) {
      return { type: 1, value: parseInt(numberMatch[1], 10), name: gironeName };
    }

    // Cerca lettera singola (es. "A", "B")
    const letterMatch = gironeName.match(/^([A-Z])$/i);
    if (letterMatch) {
      return { type: 2, value: letterMatch[1].toUpperCase().charCodeAt(0), name: gironeName };
    }

    // Altrimenti usa ordine alfabetico
    return { type: 3, value: 0, name: gironeName };
  };

  // Estrae il nome base delle dirette (parte prima del primo "-")
  const getDiretteBaseName = (nome) => {
    if (!nome) return 'Dirette';
    const dashIndex = nome.indexOf('-');
    return dashIndex > 0 ? nome.substring(0, dashIndex) : nome;
  };

  // Raggruppa le partite per girone
  const getMatchesByGirone = () => {
    const gironiMap = new Map();
    const diretteMap = new Map();

    matches.forEach(match => {
      const tipo = match.tipo?.toLowerCase() || '';
      const nome = match.nome || '';

      if (tipo === 'dirette') {
        // È una diretta: raggruppa per nome base (es. "Fase 8-4-5" da "Fase 8-4-5-F1-2")
        const baseName = getDiretteBaseName(nome);
        const fase = match.fase ?? 0;
        if (!diretteMap.has(baseName)) {
          diretteMap.set(baseName, { fase, matches: [] });
        }
        diretteMap.get(baseName).matches.push(match);
      } else if (tipo === 'girone') {
        // È un girone: raggruppa per nome completo con prefisso "Girone"
        const gironeName = `Girone ${nome}`;
        if (!gironiMap.has(gironeName)) {
          gironiMap.set(gironeName, []);
        }
        gironiMap.get(gironeName).push(match);
      } else {
        // Tipo sconosciuto: raggruppa per nome
        const key = nome || 'Senza Categoria';
        if (!gironiMap.has(key)) {
          gironiMap.set(key, []);
        }
        gironiMap.get(key).push(match);
      }
    });

    // Ordina gironi per tipo e valore
    const gironiArray = Array.from(gironiMap.entries())
      .sort((a, b) => {
        // Estrae il nome senza "Girone " per il confronto
        const nameA = a[0].replace(/^Girone\s+/, '');
        const nameB = b[0].replace(/^Girone\s+/, '');
        const typeA = getGironeType(nameA);
        const typeB = getGironeType(nameB);

        if (typeA.type !== typeB.type) {
          return typeA.type - typeB.type;
        }

        return typeA.value - typeB.value;
      });

    // Ordina dirette per numero fase
    const diretteArray = Array.from(diretteMap.entries())
      .sort((a, b) => a[1].fase - b[1].fase)
      .map(([key, value]) => [key, value.matches]);

    return [...gironiArray, ...diretteArray];
  };

  // Raggruppa le partite di un girone per turno
  const getMatchesByTurno = (gironeMatches) => {
    const turniMap = new Map();

    gironeMatches.forEach(match => {
      const turno = match.turno;
      if (!turniMap.has(turno)) {
        turniMap.set(turno, []);
      }
      turniMap.get(turno).push(match);
    });

    // Converti in array e ordina per numero turno
    return Array.from(turniMap.entries())
      .sort((a, b) => a[0] - b[0]);
  };

  if (loading) {
    return (
      <div className="risultati-page">
        <div className="risultati-loading">
          <div className="loading-spinner"></div>
          <p>Caricamento risultati...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="risultati-page">
        <div className="risultati-error">
          <h3>Errore</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const gironiData = getMatchesByGirone();

  return (
    <div className="risultati-page">
      <div className="risultati-header">
        <h1>Risultati</h1>
        {edizioneSelezionata && (
          <span className="edizione-name">{edizioneSelezionata.nome}</span>
        )}
      </div>

      {deletingMatch && (
        <div className="delete-overlay" onClick={() => setDeletingMatch(null)}>
          <div className="delete-dialog" onClick={e => e.stopPropagation()}>
            <h3>Conferma eliminazione</h3>
            <p>Eliminare la partita <strong>{deletingMatch.squadra1}</strong> vs <strong>{deletingMatch.squadra2}</strong>?</p>
            <p className="delete-warning">Verranno eliminate anche tutte le statistiche associate.</p>
            <div className="delete-actions">
              <button className="btn-cancel" onClick={() => setDeletingMatch(null)}>Annulla</button>
              <button className="btn-confirm-delete" onClick={() => handleDeleteMatch(deletingMatch)}>Elimina</button>
            </div>
          </div>
        </div>
      )}

      <div className="risultati-container">
        {gironiData.length === 0 ? (
          <div className="no-results">
            <p>Nessuna partita disponibile</p>
          </div>
        ) : (
          gironiData.map(([girone, gironeMatches]) => (
            <div key={girone} className="girone-expander">
              <div
                className="girone-header"
                onClick={() => toggleGirone(girone)}
                style={{ background: getGironeColor(girone) }}
              >
                <span className="girone-title">{girone}</span>
                <span className="girone-count">{gironeMatches.length} partite</span>
                <i className={`fas fa-chevron-${expandedGironi[girone] ? 'up' : 'down'}`}></i>
              </div>

              {expandedGironi[girone] && (
                <div className="girone-content">
                  {getMatchesByTurno(gironeMatches).map(([turno, turnoMatches]) => {
                    const editKey = `${girone}_${turno}`;
                    const isEditing = editingTurni[editKey];
                    return (
                      <div key={turno} className="turno-section">
                        <table className="turno-table">
                          <colgroup>
                            <col style={{ width: isEditing ? '7%' : '8%' }} />
                            <col style={{ width: isEditing ? '24%' : '26%' }} />
                            <col style={{ width: isEditing ? '24%' : '26%' }} />
                            <col style={{ width: isEditing ? '37%' : '40%' }} />
                            {isEditing && <col style={{ width: '8%' }} />}
                          </colgroup>
                          <thead>
                            <tr>
                              <th colSpan={isEditing ? 5 : 4} className="turno-header">
                                <div className="turno-header-content">
                                  <span>Turno {turno}</span>
                                  <i
                                    className={`fas fa-pencil-alt turno-edit-btn ${isEditing ? 'active' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); toggleEditTurno(girone, turno); }}
                                    title={isEditing ? 'Esci dalla modifica' : 'Modifica'}
                                  ></i>
                                </div>
                              </th>
                            </tr>
                            <tr>
                              <th className="col-campo">C</th>
                              <th className="col-squadra">Squadra 1</th>
                              <th className="col-squadra">Squadra 2</th>
                              <th className="col-risultato">Risultato</th>
                              {isEditing && <th className="col-azioni"></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {turnoMatches
                              .sort((a, b) => a.campo - b.campo)
                              .map((match, idx) => {
                                const winner = getWinner(match.risultato);
                                return (
                                  <tr key={idx} className={match.risultato ? 'has-result' : ''}>
                                    <td className="col-campo">{match.campo}</td>
                                    <td className={`col-squadra ${winner === 1 ? 'winner' : ''}`}>{match.squadra1}</td>
                                    <td className={`col-squadra ${winner === 2 ? 'winner' : ''}`}>{match.squadra2}</td>
                                    <td className="col-risultato">
                                      <span className={winner === 1 ? 'winner' : ''}>{formatRisultato1(match.risultato)}</span>
                                      {match.risultato && ' / '}
                                      <span className={winner === 2 ? 'winner' : ''}>{formatRisultato2(match.risultato)}</span>
                                    </td>
                                    {isEditing && (
                                      <td className="col-azioni">
                                        {match.risultato && (
                                          <i
                                            className="fas fa-trash-alt delete-match-btn"
                                            onClick={() => setDeletingMatch(match)}
                                            title="Elimina partita"
                                          ></i>
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ARisultati;
